import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  History,
  Search,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { z } from "zod";
import { useAnalyticalContext } from "../analytical-context";
import { Confidence, PageHeader } from "../components";
import { evidenceContext, matchesProduct } from "../demo-scope";
import { demoPrincipal, queryEvidence } from "../evidence-access";

const originSchema = z.string().max(120).catch("Material change investigation");
export function EvidencePage() {
  const { filters, query: contextQuery } = useAnalyticalContext();
  const origin = originSchema.parse(
    new URLSearchParams(window.location.search).get("origin") ?? undefined,
  );
  const [text, setText] = useState("");
  const [channel, setChannel] = useState("All");
  const [mode, setMode] = useState("All");
  const [expanded, setExpanded] = useState<string>();
  const [dispute, setDispute] = useState<string>();
  const [reason, setReason] = useState("");
  const [audit, setAudit] = useState<string[]>([]);
  const [showAll, setShowAll] = useState(false);
  const records = useMemo(() => queryEvidence(demoPrincipal), []);
  const investigation = evidenceContext(origin);
  const filtered = useMemo(
    () =>
      records.filter(
        (item) =>
          matchesProduct(item, filters.product) &&
          (investigation.drivers.length === 0 ||
            investigation.drivers.includes(item.contactDriver)) &&
          (channel === "All" || item.channel === channel) &&
          (mode === "All" || item.evidenceMode === mode) &&
          `${item.summary} ${item.intent} ${item.themePath.join(" ")} ${item.contactDriver}`
            .toLowerCase()
            .includes(text.toLowerCase()),
      ),
    [records, filters.product, investigation.drivers, channel, mode, text],
  );
  const href = (p: string) => `${p}${contextQuery ? `?${contextQuery}` : ""}`;
  const visible = showAll ? filtered : filtered.slice(0, 12);
  const submit = () => {
    if (!dispute || reason.trim().length < 5) return;
    setAudit((v) => [
      `Correction requested for ${dispute}: ${reason} · 18 Jul 10:31`,
      ...v,
    ]);
    setDispute(undefined);
    setReason("");
  };
  return (
    <>
      <PageHeader
        eyebrow="Evidence · Can I verify this conclusion without losing context or violating permissions?"
        title="Permission-aware interaction evidence"
        description="Structured summaries and important moments verify aggregate claims. Original content remains in the source system and source access is evaluated before disclosure."
      />
      <nav
        className="evidence-breadcrumb"
        aria-label="Investigation breadcrumb"
      >
        <a href={href("/overview")}>Overview</a>
        <span>/</span>
        <strong>{origin}</strong>
        <span>/</span>
        <span>{filtered.length} permitted evidence records</span>
      </nav>
      <section className="evidence-controls">
        <div className="inline-search inline-search--large">
          <Search size={16} />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Search summaries, intents, themes and drivers"
          />
        </div>
        <select
          aria-label="Channel"
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
        >
          <option value="All">All channels</option>
          <option>Phone</option>
          <option>Email</option>
          <option>Chat</option>
          <option>Case</option>
          <option>App</option>
        </select>
        <select
          aria-label="Evidence type"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          <option value="All">All evidence</option>
          <option>supporting</option>
          <option>contradictory</option>
          <option>insufficient</option>
        </select>
      </section>
      <div className="evidence-count">
        <span>
          Showing {visible.length} of {filtered.length} representative
          interactions
        </span>
        <span>
          <ShieldCheck size={14} /> Organisation and source permission evaluated
          in the query layer
        </span>
      </div>
      <section className="panel evidence-ledger">
        <div className="evidence-ledger__header">
          <span>Interaction summary</span>
          <span>Theme / driver</span>
          <span>Outcome</span>
          <span>Trust</span>
          <span />
        </div>
        {visible.map((item) => (
          <article
            key={item.id}
            className={
              item.permissionState === "denied" ? "permission-denied" : ""
            }
          >
            <button
              type="button"
              className="evidence-ledger__row"
              onClick={() =>
                setExpanded((v) => (v === item.id ? undefined : item.id))
              }
            >
              <span>
                <strong>{item.summary}</strong>
                <small>
                  {item.id} · {item.channel} ·{" "}
                  {new Date(item.occurredAt).toLocaleString("en-GB")}
                </small>
              </span>
              <span>
                {item.themePath.join(" › ")}
                <small>{item.contactDriver}</small>
              </span>
              <span>
                {item.outcome}
                <small>Effort {item.effort.inferred}/10 · inferred</small>
              </span>
              <span>
                <span
                  className={`evidence-mode evidence-mode--${item.evidenceMode}`}
                >
                  {item.evidenceMode}
                </span>
                <Confidence value={item.confidence.value} />
              </span>
              {expanded === item.id ? (
                <ChevronUp size={15} />
              ) : (
                <ChevronDown size={15} />
              )}
            </button>
            {expanded === item.id && (
              <div className="evidence-detail">
                {item.permissionState === "denied" ? (
                  <div className="permission-message">
                    <ShieldAlert size={18} />
                    <div>
                      <strong>Evidence access denied</strong>
                      <p>
                        {item.permissionReason} Aggregate contribution remains
                        visible, but source content and reference were not
                        returned.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <span>Intent</span>
                      <strong>{item.intent}</strong>
                    </div>
                    <div>
                      <span>Important moments</span>
                      {item.importantMoments.map((moment) => (
                        <p key={moment}>{moment}</p>
                      ))}
                    </div>
                    <div>
                      <span>Provenance</span>
                      <p>
                        {item.sourceSystem} · {item.sourceReference}
                      </p>
                      <p>
                        {item.modelVersion} · {item.instructionVersion} ·{" "}
                        {item.taxonomyVersion}
                      </p>
                      <p>
                        Processed{" "}
                        {new Date(item.processedAt).toLocaleString("en-GB")}
                      </p>
                    </div>
                    <div>
                      <span>Confidence reason</span>
                      <p>
                        {item.confidence.reason} ·{" "}
                        {item.confidence.evidenceCoverage}% evidence coverage
                      </p>
                    </div>
                    <div className="evidence-actions">
                      {item.sourceReference && (
                        <button type="button">
                          <ExternalLink size={13} /> Open in Dynamics
                        </button>
                      )}
                      <button type="button" onClick={() => setDispute(item.id)}>
                        Dispute or correct
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            {dispute === item.id && (
              <div className="correction-form">
                <label>
                  Reason for correction
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Describe the classification or interpretation issue"
                  />
                </label>
                <button
                  type="button"
                  className="primary-button"
                  onClick={submit}
                  disabled={reason.trim().length < 5}
                >
                  Add audited correction
                </button>
              </div>
            )}
          </article>
        ))}
        {filtered.length > 12 && (
          <div className="evidence-ledger__more">
            <button type="button" onClick={() => setShowAll((value) => !value)}>
              {showAll
                ? "Show representative sample"
                : `Show all ${filtered.length} interactions`}
            </button>
          </div>
        )}
      </section>
      {audit.length > 0 && (
        <section className="audit-strip">
          <History size={14} />
          <strong>Local audit</strong>
          {audit.map((x) => (
            <span key={x}>{x}</span>
          ))}
        </section>
      )}
    </>
  );
}
